import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { Volume2, VolumeX, User, Download } from 'lucide-react';
import AIService from '../../services/AIService';

const NMPLogo = require('../../Images/NMP.png');

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'skybot';
  timestamp: Date;
  audioUrl?: string;
  autoPlay?: boolean;
  downloadLink?: string;
}

interface ChatMessageProps {
  message: Message;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Debug: Log message props
  useEffect(() => {
    if (message.sender === 'skybot') {
      console.log('🤖 SkyBot message props:', {
        id: message.id,
        hasAudio: !!message.audioUrl,
        hasDownload: !!message.downloadLink,
        downloadLink: message.downloadLink,
        textPreview: message.text.substring(0, 100)
      });
    }
  }, [message]);

  // Handle document download
  const handleDocumentDownload = (downloadLink: string, fileName: string) => {
    try {
      if (downloadLink.startsWith('data:')) {
        // Handle base64 data URLs
        const link = document.createElement('a');
        link.href = downloadLink;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Handle regular URLs
        window.open(downloadLink, '_blank');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Error al descargar el documento. Por favor, inténtalo de nuevo.');
    }
  };

  // Handle Excel download
  const handleDownload = async () => {
    if (!message.downloadLink) return;
    
    try {
      setIsDownloading(true);
      await AIService.downloadExcelReport(message.downloadLink);
    } catch (error) {
      console.error('Error downloading Excel:', error);
      alert('Error al descargar el reporte Excel. Por favor, inténtalo de nuevo.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Auto-play audio if it's a voice response
  useEffect(() => {
    if (message.audioUrl && message.autoPlay && !isPlaying) {
      // Small delay to ensure the message is rendered
      const timer = setTimeout(() => {
        playAudio();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [message.audioUrl, message.autoPlay]);

  const playAudio = () => {
    if (message.audioUrl) {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }

      const newAudio = new Audio(message.audioUrl);
      setAudio(newAudio);
      setIsPlaying(true);

      newAudio.onended = () => {
        setIsPlaying(false);
      };

      newAudio.onerror = () => {
        setIsPlaying(false);
        console.error('Error playing audio');
      };

      newAudio.play().catch(error => {
        console.error('Error playing audio:', error);
        setIsPlaying(false);
      });
    }
  };

  const stopAudio = () => {
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <MessageContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      isUser={message.sender === 'user'}
    >
      <Avatar isUser={message.sender === 'user'}>
        {message.sender === 'user' ? (
          <User size={24} color="white" />
        ) : (
          <img src={NMPLogo} alt="NMP" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
        )}
      </Avatar>
      
      <MessageContent isUser={message.sender === 'user'}>
        <MessageText>{message.text}</MessageText>
        
        {/* Audio Controls */}
        {message.audioUrl && (
          <AudioControls>
            <AudioButton
              onClick={isPlaying ? stopAudio : playAudio}
              isPlaying={isPlaying}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isPlaying ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </AudioButton>
            <AudioIndicator isPlaying={isPlaying}>
              {isPlaying && (
                <>
                  <SoundWave delay="0s" />
                  <SoundWave delay="0.1s" />
                  <SoundWave delay="0.2s" />
                </>
              )}
            </AudioIndicator>
          </AudioControls>
        )}

        {/* Document Download Controls */}
        {message.downloadLink && message.downloadLink.includes('/api/documents/download/') && (
          <DownloadControls>
            <DownloadButton
              onClick={() => handleDocumentDownload(message.downloadLink!, 'documento.pdf')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Download size={20} />
            </DownloadButton>
            <DownloadIndicator>
              Descargar PDF
            </DownloadIndicator>
          </DownloadControls>
        )}

        {/* Excel Download Controls */}
        {(message.downloadLink || (message.sender === 'skybot' && message.text.includes('Excel'))) && (
          <DownloadControls>
            <DownloadButton
              onClick={handleDownload}
              disabled={isDownloading || !message.downloadLink}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Download size={20} />
            </DownloadButton>
            <DownloadIndicator>
              {!message.downloadLink ? 'Generando enlace...' :
               isDownloading ? 'Descargando...' : 'Descargar Excel'}
            </DownloadIndicator>
          </DownloadControls>
        )}

        {/* Debug: Mostrar siempre si es SkyBot para verificar */}
        {message.sender === 'skybot' && !message.downloadLink && (
          <div style={{
            marginTop: '1rem',
            padding: '0.5rem',
            background: 'rgba(255, 0, 0, 0.1)',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#ff6666'
          }}>
            🔍 Debug: No download link available
          </div>
        )}
      </MessageContent>
    </MessageContainer>
  );
};

// Keyframes
const liquidSlide = keyframes`
  0% { 
    opacity: 0; 
    transform: translateY(50px) scale(0.8); 
    filter: blur(10px);
  }
  60% { 
    transform: translateY(-5px) scale(1.02); 
    filter: blur(2px);
  }
  100% { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
    filter: blur(0px);
  }
`;

const energyRing = keyframes`
  0% { transform: rotate(0deg) scale(1); opacity: 0.7; }
  50% { transform: rotate(180deg) scale(1.1); opacity: 0.3; }
  100% { transform: rotate(360deg) scale(1); opacity: 0.7; }
`;

const wave = keyframes`
  0%, 100% {
    height: 8px;
  }
  50% {
    height: 20px;
  }
`;

// Styled Components
const MessageContainer = styled(motion.div)<{ isUser: boolean }>`
  display: flex;
  gap: 15px;
  animation: ${liquidSlide} 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  flex-direction: ${props => props.isUser ? 'row-reverse' : 'row'};
`;

const Avatar = styled.div<{ isUser: boolean }>`
  width: 55px;
  height: 55px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
  flex-shrink: 0;
  position: relative;
  font-size: 14px;
  background: ${props => props.isUser 
    ? 'linear-gradient(135deg, #686869, #000)' 
    : 'linear-gradient(135deg, #98012E, #CA7C8E)'};

  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border-radius: 50%;
    background: ${props => props.isUser 
      ? 'conic-gradient(from 0deg, #686869, #333, #000, #686869)' 
      : 'conic-gradient(from 0deg, #98012E, #CA7C8E, #E9C9CE, #98012E)'};
    animation: ${energyRing} 3s linear infinite ${props => props.isUser ? 'reverse' : ''};
    z-index: -1;
  }
`;

const MessageContent = styled.div<{ isUser: boolean }>`
  max-width: 70%;
  padding: 18px 24px;
  border-radius: 25px;
  position: relative;
  word-wrap: break-word;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  background: ${props => props.isUser 
    ? 'linear-gradient(135deg, #98012E, #CA7C8E)' 
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(233, 201, 206, 0.95))'};
  color: ${props => props.isUser ? 'white' : '#2c0a0f'};
  border-bottom-${props => props.isUser ? 'right' : 'left'}-radius: 8px;
  box-shadow: ${props => props.isUser 
    ? '0 5px 15px rgba(0, 0, 0, 0.3)' 
    : '0 5px 15px rgba(152, 1, 46, 0.2)'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const MessageText = styled.p`
  margin: 0;
  line-height: 1.5;
  font-size: 1rem;
  word-wrap: break-word;
`;

const AudioControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const AudioButton = styled(motion.button).withConfig({
  shouldForwardProp: (prop) => prop !== 'isPlaying'
})<{ isPlaying: boolean }>`
  width: 48px;
  height: 48px;
  border: none;
  background: ${props => props.isPlaying 
    ? 'linear-gradient(135deg, #ff4444, #ff6666)'
    : 'linear-gradient(135deg, #CA7C8E, #E9C9CE)'};
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  font-size: 16px;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 8px 20px rgba(202, 124, 142, 0.5);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const AudioIndicator = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isPlaying'
})<{ isPlaying: boolean }>`
  display: flex;
  align-items: center;
  gap: 2px;
  opacity: ${props => props.isPlaying ? 1 : 0};
  transition: opacity 0.3s ease;
`;

const SoundWave = styled.div<{ delay: string }>`
  width: 3px;
  height: 20px;
  background: #98012E;
  border-radius: 2px;
  animation: ${wave} 1s infinite ease-in-out;
  animation-delay: ${props => props.delay};
`;

// Download Controls Styles
const DownloadControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const DownloadButton = styled(motion.button)`
  width: 48px;
  height: 48px;
  border: none;
  background: linear-gradient(135deg, #28a745, #34ce57);
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  font-size: 16px;

  &:hover:not(:disabled) {
    transform: scale(1.1);
    box-shadow: 0 8px 20px rgba(40, 167, 69, 0.5);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: linear-gradient(135deg, #6c757d, #868e96);
  }
`;

const DownloadIndicator = styled.div`
  color: #28a745;
  font-size: 14px;
  font-weight: 500;
  opacity: 0.8;
`;

export default ChatMessage;
